import type { OfflineDbDriver } from './driver-types';

/**
 * The encrypted native-data schema.
 *
 * Server-derived families remain disposable, but `local_drafts` contains
 * user-authored recovery snapshots. Known schema versions therefore migrate
 * forward without dropping the file. Unknown versions still fail closed by
 * recreating the encrypted database. A future outbox is a different state
 * machine and still gets its own migrated database rather than sharing this
 * cache/draft lifecycle.
 *
 * Column conventions:
 * - `payload` is the cached server row as JSON. The server owns row shape;
 *   mirroring columns here would turn every server change into a version
 *   bump. Columns exist only where SQL needs them: identity, org scoping,
 *   ordering, eviction accounting.
 * - Org-scoped families (see ORG_SCOPED_TABLES) carry `organization_id`
 *   and their payload reads filter on it; `meta` and `bootstrap_profile`
 *   are device/user-scoped by nature. `account_id` uses '' (never NULL)
 *   for org-scoped rows: SQLite permits NULL in composite PRIMARY KEYs and
 *   treats each NULL as distinct, which would let duplicate sync_state
 *   rows accumulate.
 * - The credential axis NEVER lands here, even encrypted: email-account
 *   rows carry METADATA only (id, address, provider, status) - no tokens,
 *   no secrets of any kind.
 */
export const SCHEMA_VERSION = 2;

const LOCAL_DRAFTS_TABLE = `CREATE TABLE IF NOT EXISTS local_drafts (
  organization_id TEXT NOT NULL,
  kind TEXT NOT NULL,
  updated_at INTEGER NOT NULL,
  payload TEXT NOT NULL,
  PRIMARY KEY (organization_id, kind)
)`;
const LOCAL_DRAFTS_INDEX =
  'CREATE INDEX IF NOT EXISTS idx_local_drafts_org_updated ON local_drafts (organization_id, updated_at DESC)';

const TABLES = [
  `CREATE TABLE IF NOT EXISTS meta (
    key TEXT PRIMARY KEY,
    value TEXT NOT NULL
  )`,
  `CREATE TABLE IF NOT EXISTS bootstrap_profile (
    user_id TEXT PRIMARY KEY,
    payload TEXT NOT NULL
  )`,
  `CREATE TABLE IF NOT EXISTS org_memberships (
    organization_id TEXT PRIMARY KEY,
    payload TEXT NOT NULL
  )`,
  `CREATE TABLE IF NOT EXISTS email_accounts_meta (
    id TEXT PRIMARY KEY,
    organization_id TEXT NOT NULL,
    payload TEXT NOT NULL
  )`,
  `CREATE TABLE IF NOT EXISTS email_index (
    email_metadata_id TEXT PRIMARY KEY,
    organization_id TEXT NOT NULL,
    account_id TEXT NOT NULL DEFAULT '',
    sort_ts INTEGER NOT NULL DEFAULT 0,
    synced_at INTEGER NOT NULL,
    payload TEXT NOT NULL
  )`,
  `CREATE TABLE IF NOT EXISTS email_bodies (
    email_metadata_id TEXT PRIMARY KEY,
    organization_id TEXT NOT NULL,
    bytes INTEGER NOT NULL DEFAULT 0,
    last_access INTEGER NOT NULL DEFAULT 0,
    synced_at INTEGER NOT NULL,
    payload TEXT NOT NULL
  )`,
  `CREATE TABLE IF NOT EXISTS tasks (
    id TEXT PRIMARY KEY,
    organization_id TEXT NOT NULL,
    synced_at INTEGER NOT NULL,
    payload TEXT NOT NULL
  )`,
  `CREATE TABLE IF NOT EXISTS notifications (
    id TEXT PRIMARY KEY,
    organization_id TEXT NOT NULL,
    synced_at INTEGER NOT NULL,
    payload TEXT NOT NULL
  )`,
  LOCAL_DRAFTS_TABLE,
  `CREATE TABLE IF NOT EXISTS sync_state (
    organization_id TEXT NOT NULL,
    account_id TEXT NOT NULL DEFAULT '',
    scope TEXT NOT NULL,
    last_synced_at INTEGER NOT NULL,
    cursor TEXT,
    PRIMARY KEY (organization_id, account_id, scope)
  )`,
];

const INDEXES = [
  'CREATE INDEX IF NOT EXISTS idx_email_index_org_sort ON email_index (organization_id, sort_ts DESC)',
  'CREATE INDEX IF NOT EXISTS idx_email_bodies_org_access ON email_bodies (organization_id, last_access)',
  'CREATE INDEX IF NOT EXISTS idx_tasks_org ON tasks (organization_id)',
  'CREATE INDEX IF NOT EXISTS idx_notifications_org ON notifications (organization_id)',
  LOCAL_DRAFTS_INDEX,
];

export const ALL_TABLE_NAMES = [
  'meta',
  'bootstrap_profile',
  'org_memberships',
  'email_accounts_meta',
  'email_index',
  'email_bodies',
  'tasks',
  'notifications',
  'local_drafts',
  'sync_state',
] as const;

/**
 * Every table carrying an `organization_id` column - the purge iterates
 * THIS list, not a hand-typed copy, so adding an org-scoped table to the
 * schema automatically adds it to the tenancy purge. `satisfies` pins each
 * name to a real table.
 */
export const ORG_SCOPED_TABLES = [
  'org_memberships',
  'email_accounts_meta',
  'email_index',
  'email_bodies',
  'tasks',
  'notifications',
  'local_drafts',
  'sync_state',
] as const satisfies readonly (typeof ALL_TABLE_NAMES)[number][];

/** Double-quote an identifier for interpolation into DDL. */
export function quoteIdentifier(name: string): string {
  return `"${name.replaceAll('"', '""')}"`;
}

/**
 * Bring the freshly-opened database to the current schema. Version 1 shipped
 * before user-authored local drafts existed, so its forward migration adds the
 * durable table without disturbing cached rows. Unknown versions recreate the
 * database because their shape cannot be safely interpreted.
 */
export async function ensureSchema(driver: OfflineDbDriver): Promise<void> {
  const rows = await driver.query<{ user_version: number }>('PRAGMA user_version');
  const current = rows[0]?.user_version ?? 0;

  if (current === SCHEMA_VERSION) {
    return;
  }

  if (current === 1) {
    await driver.run(LOCAL_DRAFTS_TABLE);
    await driver.run(LOCAL_DRAFTS_INDEX);
    await driver.run(`PRAGMA user_version = ${SCHEMA_VERSION}`);
    return;
  }

  if (current !== SCHEMA_VERSION) {
    // Drop what the database ACTUALLY contains, not what this version knows
    // about: an older schema may hold tables this list no longer names, and
    // "drops everything" must mean everything or stale rows outlive their
    // version.
    const existing = await driver.query<{ name: string }>(
      "SELECT name FROM sqlite_master WHERE type = 'table' AND name NOT LIKE 'sqlite_%'",
    );
    const toDrop = new Set([...ALL_TABLE_NAMES, ...existing.map((row) => row.name)]);
    for (const table of toDrop) {
      await driver.run(`DROP TABLE IF EXISTS ${quoteIdentifier(table)}`);
    }
    for (const ddl of TABLES) {
      await driver.run(ddl);
    }
    for (const ddl of INDEXES) {
      await driver.run(ddl);
    }
    await driver.run(`PRAGMA user_version = ${SCHEMA_VERSION}`);
  }
}
